import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from "react-router-dom";
import axiosInstance from './../axiosInstance.js';
import {localizeKey, localizeArt} from './../localize.js';
import MyUserContext from './../myUserContext.js';
import CopyTextButton from './CopyTextButton';
import AvatarUpload from './AvatarUpload';
import ExpandableImage from './ExpandableImage';
import ToggleSwitch from './ToggleSwitch';
import Collapsible from './Collapsible';
import { SegmentedControl, useMantineColorScheme } from '@mantine/core';

const Settings = ({ onSettingsChange, onLogout }) => {
  const myUser = useContext(MyUserContext);
  const { colorScheme, setColorScheme } = useMantineColorScheme();

  const navigate = useNavigate();

  const [selectedLanguage, setSelectedLanguage] = useState('en'); // Default language
  const [selectedUserName, setSelectedUserName] = useState(myUser.myUserSettings.userName);
  const [isLoading, setIsLoading] = useState(true); // State to manage loading state
  const [error, setError] = useState(null); // State to manage errors
  const [tgToken, setTgToken] = useState(null);
  const [userNameInput, setUsernameInput] = useState(myUser.myUserSettings.userName);
  const [selectedIsPrivateAccount, setSelectedIsPrivateAccount] = useState(myUser.myUserSettings.privateAccount);
  const [selectedAutoSubscribeToFriendsChats, setSelectedAutoSubscribeToFriendsChats] = useState(
    myUser?.myUserSettings?.autoSubscribeToFriendsChats ?? true
  );


  const [userSettings, setUserSettings] = useState(null);

  // Add an interceptor to add authorization header to requests
  axiosInstance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('jwtToken'); // Get token from local storage
      if (token) {
        config.headers.Authorization = `Bearer ${token}`; // Add authorization header
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  useEffect(() => {
    // Fetch initial language setting when the component mounts
    axiosInstance
      .get('/user/settings')
      .then((response) => {
        const { lang, privateAccount, autoSubscribeToFriendsChats } = response.data;
        setSelectedLanguage(lang); // TODO: do not process it independently
        setSelectedIsPrivateAccount(privateAccount);
        setSelectedAutoSubscribeToFriendsChats(autoSubscribeToFriendsChats ?? true);
        setUserSettings(response.data);
        setIsLoading(false); // Update loading state after successful response
      })
      .catch((error) => {
        setError(error.message); // Set error state with error message
        setIsLoading(false); // Update loading state after error
      });
  }, []); // Empty dependency array ensures this effect runs only once when the component mounts


  const handleLanguageChange = (newLanguage) => {
    setIsLoading(true); // Set loading state to true

    axiosInstance
    .post('/user/settings', { lang: newLanguage })
    .then((response) => {
      const { lang } = response.data;
      setSelectedLanguage(lang); // Update language state with the response
      onSettingsChange(lang, response.data.userName, response.data.avatarUrl);
      setError(null); // Clear any previous errors
    })
    .catch((error) => {
      setError(error.message); // Set error state with error message
    })
    .finally(() => {
      setIsLoading(false); // Set loading state to false regardless of success or failure
    });
  };

  const generateTgToken = () => {
    //setIsLoading(true);
    axiosInstance
    .post('/user/generateTelegramToken', {})
    .then((response) => {
      setTgToken(response.data.token);
      setTimeout(()=>{
         setTgToken(null);
      }, 20000);
    })
    .catch((error) => {
      setError(error.message); // Set error state with error message
    })
    .finally(() => {
      //setIsLoading(false); // Set loading state to false regardless of success or failure
    });
  }

  const handleUsernameInputChange = (event) => {
    setUsernameInput(event.target.value);
  };
  const handleUsernameChange = () => {
    axiosInstance
    .post('/user/settings', { userName: userNameInput })
    .then((response) => {
      setSelectedUserName(response.data.userName);
      onSettingsChange(response.data.lang, response.data.userName, response.data.avatarUrl);
      setError(null); // Clear any previous errors
    })
    .catch((error) => {
      setError(error.message); // Set error state with error message
    })
    .finally(() => {
      setIsLoading(false); // Set loading state to false regardless of success or failure
    });
  }

  const handleAccountIsPrivate = (isPrivate) => {
    axiosInstance
    .post('/user/settings', { privateAccount: isPrivate })
    .then((response) => {
      setSelectedIsPrivateAccount(isPrivate);
      setUserSettings(response.data);
      setError(null);
    })
    .catch((error) => {
      setError(error.message);
    })
    .finally(() => {
      setIsLoading(false); // TODO: get rid of it
    })
  }

  const handleAutoSubscribeToFriendsChats = (shouldAutoSubscribe) => {
    axiosInstance
    .post('/user/settings', { autoSubscribeToFriendsChats: shouldAutoSubscribe })
    .then((response) => {
      setSelectedAutoSubscribeToFriendsChats(shouldAutoSubscribe);
      setUserSettings(response.data);
      setError(null);
    })
    .catch((error) => {
      setError(error.message);
    })
    .finally(() => {
      setIsLoading(false); // TODO: get rid of it
    })
  }

  const settingsAvatarUrlChange = (url) => {
    onSettingsChange(selectedLanguage, selectedUserName, url);
  }

  const handleRecalcClick = (smart) => {
    const userId = myUser?.myUserInfo?.myUserId;
    if (!userId) {
      alert('No user ID found');
      return;
    }

    axiosInstance.post(`/user/${userId}/recalc${smart ? '?smart=true' : ''}`)
      .then(response => {
        alert('Recalculation started successfully');
      })
      .catch(error => {
        alert('Could not start recalculation');
      });
  };

  const handleUserMatchRecalcClick = () => {
    axiosInstance.post(`/userMatch/recalc`)
      .then(response => {
        alert('User match recalculation started successfully');
      })
      .catch(error => {
        alert('Could not start user match recalculation');
      });
  };

  const handleHotRecalcClick = () => {
    axiosInstance.post(`/recalc/hot`)
      .then(response => {
        alert('Hot recalculation started successfully');
      })
      .catch(error => {
        alert('Could not start hot recalculation');
      });
  };

  const language = myUser ? myUser.myUserSettings.lang : 'ru';

  if (isLoading) {
    return <div></div>;
  }

  if (error) {
    return <p>Error: {error}</p>;
  }

  return (
    <div className="container mx-auto">
      <div className="flex items-center ml-4 mt-4">
        <img
          src="/back_icon.png"
          alt="Back"
          className="cursor-pointer w-6 h-6"
          onClick={() => navigate(-1)} // Go back in history
        />
      </div>
      <br/>
      {isLoading && <p></p>}
      {error && <p>Error: {error}</p>}

      <h1 className="flex justify-center text-2xl font-bold mb-8">
        {language === 'ru' ? 'Настройки' : 'Settings'}
      </h1>

      <div className="flex justify-center items-center">
        <input
          className="text-sm rounded-lg block p-2.5"
          style={{ backgroundColor: 'var(--mantine-color-default)', color: 'var(--mantine-color-text)', border: '1px solid var(--mantine-color-default-border)' }}
          type="text"
          value={userNameInput}
          onChange={handleUsernameInputChange}
        />
        &nbsp;
        <button onClick={handleUsernameChange}>{localizeKey('setUserName', selectedLanguage)}</button>
      </div>

      <br/>
      <div className="flex justify-center items-center">
        <AvatarUpload initUrl={userSettings.avatarUrl} onAvatarUrlChange={settingsAvatarUrlChange} />
      </div>

      <br/>

      <div className="flex justify-center">
        <SegmentedControl
          value={selectedLanguage}
          onChange={handleLanguageChange}
          disabled={isLoading}
          data={[
            { label: 'English', value: 'en' },
            { label: 'Русский', value: 'ru' },
          ]}
          radius="md"
          size="sm"
        />
      </div>

      <div className="flex justify-center mt-6">
        <SegmentedControl
          value={colorScheme}
          onChange={setColorScheme}
          data={[
            { label: selectedLanguage === 'ru' ? 'Светлая' : 'Light', value: 'light' },
            { label: selectedLanguage === 'ru' ? 'Тёмная' : 'Dark', value: 'dark' },
            { label: 'Auto', value: 'auto' },
          ]}
          radius="md"
          size="sm"
        />
      </div>

      <div className="mt-7 flex justify-center items-center">
        <ToggleSwitch isOn={selectedIsPrivateAccount} onToggle={handleAccountIsPrivate} /> &nbsp; {(selectedLanguage === 'ru') ? "Приватный аккаунт" : "Private account"}
      </div>
      <div className="flex justify-center text-center leading-none">
      {
        selectedLanguage === 'ru' ?
        "Только одобренные подписчики будут видеть вашу страницу" :
        "Only approved subscribers will see your page"
      }
      </div>

      <div className="mt-7 flex justify-center items-center">
        <ToggleSwitch isOn={selectedAutoSubscribeToFriendsChats} onToggle={handleAutoSubscribeToFriendsChats} /> &nbsp; {(selectedLanguage === 'ru') ? "Автоподписка на чаты друзей" : "Auto-subscribe to friends' chats"}
      </div>
      <div className="flex justify-center text-center leading-none">
      {
        selectedLanguage === 'ru' ?
        "Подписывать вас на чаты новых событий друзей автоматически" :
        "Automatically subscribe you to friends' new event chats"
      }
      </div>
      
      <div className="mt-7 flex justify-center">
        {localizeKey('click_cap', selectedLanguage)} <img style={{cursor: 'pointer', width: '25px', 'vertical-align': 'middle'}} onClick={generateTgToken} src="telegram_icon.png"/>{localizeKey('toGetNotifications', selectedLanguage)}
      </div>

      { 
        tgToken && 
        <>
          <div className="flex justify-center">
            <CopyTextButton
              textToCopy={
                tgToken
              }
              overrideButtonText={
                localizeKey('copySecretToken_cap', selectedLanguage)
              }
            />
          </div>
          <div className="flex justify-center">
            {localizeKey('sendIt_cap', selectedLanguage)}&nbsp;<a style={{ color: 'var(--mantine-color-blue-text)' }} href='https://t.me/wowContentLolBot' target="_blank">{localizeKey('here_direction', selectedLanguage)}</a>
          </div>
        </>  
      }

      {myUser?.myUserInfo?.myUserId === '4312f009-7502-43a6-9483-2824508fe96f' && (
        <div className="mt-7 flex justify-center flex-wrap">
          <button onClick={()=>{handleRecalcClick(false);}} style={{ margin: '10px', padding: '10px', backgroundColor: 'blue', color: 'white', border: 'none', borderRadius: '5px' }}>
            Recalculate
          </button>
          <button onClick={()=>{handleRecalcClick(true);}} style={{ margin: '10px', padding: '10px', backgroundColor: 'green', color: 'white', border: 'none', borderRadius: '5px' }}>
            Recalculate smart
          </button>
          <button onClick={()=>{handleHotRecalcClick();}} style={{ margin: '10px', padding: '10px', backgroundColor: 'gray', color: 'white', border: 'none', borderRadius: '5px' }}>
            Recalculate hots
          </button>
          <button onClick={()=>{handleUserMatchRecalcClick();}} style={{ margin: '10px', padding: '10px', backgroundColor: 'red', color: 'white', border: 'none', borderRadius: '5px' }}>
            Recalculate user match
          </button>
        </div>
      )}

      <div className="flex justify-center mt-48 hover:cursor-pointer hover:underline" style={{ color: 'var(--mantine-color-red-text)' }}>
        <div onClick={()=> {
          if (onLogout) {
            onLogout();
          } else {
            localStorage.removeItem('jwtToken');
          }
          navigate("/");
        }}>
          {language === "ru" ? "Выйти" : "Log out"}
        </div>
      </div>

      {
      selectedLanguage === "en" ? 
      <>
        <div className="mt-10 flex justify-center text-center">
          <div>Hint: for a much better experience on mobile phone, add the website to home screen in your browser.</div>
        </div>        
      </>
        :
      <>
        <div className="mt-10 flex justify-center text-center">
          <div>Рекомендация: для большего удобства на мобильных телефонах добавьте веб-сайт на домашний экран из браузера.</div>        
        </div>
        <div className="flex justify-center items-center text-center">
          Инструкция для Android: &nbsp;          
          <ExpandableImage small={true} src={"https://wowcontent.ams3.cdn.digitaloceanspaces.com/add_to_home_screen_android.jpg"} />
        </div>
        <div className="flex justify-center items-center text-center">
          Инструкция для iOS: &nbsp;          
          <ExpandableImage small={true} src={"https://wowcontent.ams3.cdn.digitaloceanspaces.com/add_to_home_screen_ios.jpg"} />
        </div>
      </> 
      }
    </div>
  );
};

export default Settings;
